import React, { useEffect, useState, useCallback,  useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Modal,
  Paper,
  Table,
  Text,
  Tooltip,
  Avatar,
  Select,
  TextInput,
  ActionIcon,
  Group,
  Loader,
  Notification,
} from '@mantine/core';
import {
  IconCheck,
  IconTrash,
  IconPencil,
  IconDeviceFloppy,
  IconRobot,
} from '@tabler/icons-react';

import AutosaveIndicator from './AutoSaveIndicator';
import IconWithProgress from './IconWithProgress';
import { generateSecurityRisks, saveSecurityRisk, fetchSecurityRisks, discardSecurityRisk } from './redux/actions/securityActions';
import loadSecurityRisks from './loadSecurityRisks';
import { validate as uuidValidate, version as uuidVersion } from 'uuid';

import { useAuth0 } from '@auth0/auth0-react';


const isValidUUIDV4 = (uuid) => uuidValidate(uuid) && uuidVersion(uuid) === 4;

const SecurityRiskCatalogue = ({ modelId, assessmentUuid, stepIndex = 0 }) => {
  const dispatch = useDispatch();
  const securityRisks = useSelector(state => state.security.models[modelId]?.securityRisks || []);
  const assessmentData = useSelector(state => state.security.models[modelId]?.assessmentData || {});

  // Accordion state removed
  const isLoading = useSelector(state => state.security.loading);
  const [editingRiskId, setEditingRiskId] = useState(null);
  const [editedRiskDetails, setEditedRiskDetails] = useState({});


const [openDialog, setOpenDialog] = useState(false);
const [currentRiskId, setCurrentRiskId] = useState(null);
const [snackbarOpen, setSnackbarOpen] = useState(false);
const [snackbarMessage, setSnackbarMessage] = useState('');
const [snackbarSeverity, setSnackbarSeverity] = useState('info');
const [rationaleText,setRationaleText] = useState('');
const [rationaleOpen,setRationaleOpen] = useState(false);
const openRationale = txt => { setRationaleText(txt); setRationaleOpen(true); };
const closeRationale = ()=> setRationaleOpen(false);

const [isSaving, setIsSaving] = useState(false);
const [isSaved, setIsSaved] = useState(false);

const { user } = useAuth0();
  // Level options for dropdowns
  const levelOptions = ['None','Negligible', 'Low','Moderate', 'Significant', 'High', 'Critical'];
  const ciaSteps = ['Confidentiality', 'Integrity', 'Availability'];
  const activeCategory = ciaSteps[stepIndex] || ciaSteps[0];


 
  useEffect(() => {
    console.log("Initiating fetch of security risks...");
    dispatch(fetchSecurityRisks(modelId, assessmentUuid, false));
}, [dispatch, modelId, assessmentUuid]);


// Remove old refresh handler
// const handleRefreshRisks = () => {
//   dispatch(generateSecurityRisks(modelId));
// };

const allCategories = useMemo(() => loadSecurityRisks(), []);
const normalizedArea = activeCategory.toLowerCase();

const handleEvaluateRisks = useCallback(
  () =>
    dispatch(
      generateSecurityRisks(
        modelId,
        assessmentUuid,
        assessmentData,
        allCategories,
        normalizedArea
      )
    ),
  [dispatch, modelId, assessmentUuid, assessmentData, allCategories, normalizedArea]
);


  const groupedRisks = useMemo(() => {
    const grouped = {};
  
    for (const category of Object.keys(allCategories)) {
      grouped[category] = allCategories[category].map(riskName => {
        // Normalize riskName and category
        const normalizedRiskName = riskName.trim().toLowerCase();
        const normalizedCategory = category.trim().toLowerCase();
  
        // Find if the risk has been evaluated
        const existingRisk = securityRisks.find(risk => {
          const riskNameMatch =
            risk.risk_name &&
            risk.risk_name.trim().toLowerCase() === normalizedRiskName;
          const categoryMatch =
            risk.category &&
            risk.category.trim().toLowerCase() === normalizedCategory;
          return riskNameMatch && categoryMatch;
        });
  
        if (existingRisk) {
          return { ...existingRisk, evaluated: 'Yes' };
        } else {
          // Risk hasn't been evaluated yet
          return {
            risk_name: riskName,
            category,
            evaluated: 'No',
            threat_level: '',
            vulnerability_level: '',
            asset_value: '',
            risk_id: null, // No risk_id yet
          };
        }
      });
    }
    return grouped;
  }, [allCategories, securityRisks]);


  console.log ('security risks to render', groupedRisks)

  const handleEditRisk = useCallback((risk) => {

    const uniqueRiskId = risk.risk_id || `${risk.category}-${risk.risk_name}`;
    setEditingRiskId(uniqueRiskId);
    //setEditingRiskId(risk.risk_id || risk.risk_name);
    setEditedRiskDetails({
      risk_name: risk.risk_name,
      category: risk.category,
      threat_level: risk.threat_level,
      vulnerability_level: risk.vulnerability_level,
      asset_value: risk.asset_value
    });
  }, []);

  const handleChangeDetail = (field, value) => {
    setEditedRiskDetails(prev => ({ ...prev, [field]: value }));
  };

  const resetEditing = useCallback(() => {
    setEditingRiskId(null);
    setEditedRiskDetails({});
  }, []);



const handleSaveRisk = useCallback(async () => {
  setIsSaving(true); // Start saving

  const risk_id = isValidUUIDV4(editingRiskId) ? editingRiskId : null;

  const riskDetails = {
    risk_id,
    risk_name: editedRiskDetails.risk_name,
    category: editedRiskDetails.category,
    threat_level: editedRiskDetails.threat_level,
    vulnerability_level: editedRiskDetails.vulnerability_level,
    asset_value: editedRiskDetails.asset_value,
    justification: editedRiskDetails.justification,
    owner: {
      name: user.name,
      email: user.email,
      picture: user.picture,
    },
  };

  console.log('Saving risk:', riskDetails);

  try {
    await dispatch(saveSecurityRisk(modelId, assessmentUuid, riskDetails));
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000); // Clear saved state after 2 seconds
  } catch (error) {
    setSnackbarMessage(`Error saving risk: ${error.message}`);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  } finally {
    setIsSaving(false); // Stop saving
  }

  resetEditing();
}, [modelId, assessmentUuid, editingRiskId, editedRiskDetails, user.name, user.email, user.picture, resetEditing, dispatch]);
 

  const handleOpenDialog = (riskId) => {
    setCurrentRiskId(riskId);
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  const handleDiscardRisk = () => {
    if (currentRiskId) {
      dispatch(discardSecurityRisk(modelId, currentRiskId))
        .then(() => {
          setSnackbarMessage('Risk discarded successfully.');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          handleCloseDialog();
        })
        .catch(error => {
          setSnackbarMessage(`Error discarding risk: ${error.message}`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    }
  };

  
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

const renderRisksByCategory = (categoryRisks, category) => (
  <Paper withBorder radius="md">
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', borderBottom: '1px solid #E5E7EB', paddingTop: 8, paddingBottom: 8 }}>
            Risk Name
          </Table.Th>
          <Table.Th style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', borderBottom: '1px solid #E5E7EB', paddingTop: 8, paddingBottom: 8 }}>
            Threat Level
          </Table.Th>
          <Table.Th style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', borderBottom: '1px solid #E5E7EB', paddingTop: 8, paddingBottom: 8 }}>
            Vulnerability Level
          </Table.Th>
          <Table.Th style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', borderBottom: '1px solid #E5E7EB', paddingTop: 8, paddingBottom: 8 }}>
            Asset Value
          </Table.Th>
          <Table.Th style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', borderBottom: '1px solid #E5E7EB', paddingTop: 8, paddingBottom: 8 }}>
            Rationale
          </Table.Th>
          <Table.Th style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', borderBottom: '1px solid #E5E7EB', paddingTop: 8, paddingBottom: 8 }}>
            Owner
          </Table.Th>
          <Table.Th style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', borderBottom: '1px solid #E5E7EB', paddingTop: 8, paddingBottom: 8 }}>
            Actions
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {categoryRisks.length > 0 ? (
          categoryRisks.map((risk) => {
            const uniqueRiskId = risk.risk_id || `${risk.category}-${risk.risk_name}`;
            return (
              <Table.Tr key={uniqueRiskId} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <Table.Td>
                  {editingRiskId === uniqueRiskId ? (
                    <TextInput
                      value={editedRiskDetails.risk_name}
                      onChange={(e) => handleChangeDetail('risk_name', e.currentTarget.value)}
                      withAsterisk={false}
                    />
                  ) : (
                    risk.risk_name
                  )}
                </Table.Td>
                <Table.Td>
                  {editingRiskId === uniqueRiskId ? (
                    <Select
                      data={levelOptions}
                      value={editedRiskDetails.threat_level || ''}
                      onChange={(value) => handleChangeDetail('threat_level', value || '')}
                      comboboxProps={{ withinPortal: true }}
                      searchable={false}
                      clearable={false}
                      size="sm"
                    />
                  ) : (
                    risk.threat_level || '-'
                  )}
                </Table.Td>
                <Table.Td>
                  {editingRiskId === uniqueRiskId ? (
                    <Select
                      data={levelOptions}
                      value={editedRiskDetails.vulnerability_level || ''}
                      onChange={(value) => handleChangeDetail('vulnerability_level', value || '')}
                      comboboxProps={{ withinPortal: true }}
                      searchable={false}
                      clearable={false}
                      size="sm"
                    />
                  ) : (
                    risk.vulnerability_level || '-'
                  )}
                </Table.Td>
                <Table.Td>
                  {editingRiskId === uniqueRiskId ? (
                    <Select
                      data={levelOptions}
                      value={editedRiskDetails.asset_value || ''}
                      onChange={(value) => handleChangeDetail('asset_value', value || '')}
                      comboboxProps={{ withinPortal: true }}
                      searchable={false}
                      clearable={false}
                      size="sm"
                    />
                  ) : (
                    risk.asset_value || '-'
                  )}
                </Table.Td>
                {/* Rationale cell */}
                <Table.Td>
                  {risk.justification ? (
                    <Tooltip label="View rationale">
                      <ActionIcon variant="subtle" onClick={() => openRationale(risk.justification)}>
                        <IconRobot size={16} />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </Table.Td>
                <Table.Td>
                  {risk.owner_name ? (
                    <Tooltip label={risk.owner_name}>
                      <Avatar alt={risk.owner_name} src={risk.owner_picture} radius="xl" />
                    </Tooltip>
                  ) : (
                    'No owner'
                  )}
                </Table.Td>
                <Table.Td>
                  {editingRiskId === uniqueRiskId ? (
                    <ActionIcon variant="light" color="green" onClick={handleSaveRisk}>
                      <IconCheck size={18} />
                    </ActionIcon>
                  ) : (
                    <Group gap="xs">
                      {risk.evaluated === 'Yes' ? (
                        <>
                          <ActionIcon variant="light" onClick={() => handleEditRisk(risk)}>
                            <IconPencil size={18} />
                          </ActionIcon>
                          {!risk.risk_id && (
                            <ActionIcon variant="light" onClick={() => handleSaveRisk(risk)}>
                              <IconDeviceFloppy size={18} />
                            </ActionIcon>
                          )}
                          <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleOpenDialog(risk.risk_id)}
                            disabled={!risk.risk_id}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </>
                      ) : (
                        <>
                          <ActionIcon variant="light" onClick={() => handleEditRisk(risk)}>
                            <IconPencil size={18} />
                          </ActionIcon>
                          <Tooltip label="Evaluate risks first">
                            <ActionIcon variant="light" disabled>
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      )}
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })
        ) : (
          <Table.Tr>
            <Table.Td colSpan={7} style={{ textAlign: 'center' }}>
              No {toTitleCase(category)} Risks
            </Table.Td>
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  </Paper>
);

// Accordion and UUID helpers removed if not used

const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
};

return (
  <div style={{ margin: 16 }}>
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        paddingTop: 12,
        marginBottom: 8,
        paddingBottom: 8,
        paddingLeft: 16,
      }}
    >
      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text size="lg" fw={600} c="#374151" style={{ letterSpacing: '-0.01em', lineHeight: 1.4 }}>
          Security Risks — {activeCategory}
        </Text>
        <AutosaveIndicator isSaving={isSaving} isSaved={isSaved} />
      </Box>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto', paddingRight: 16 }}>
        <Button
          variant="filled"
          color="dark"
          leftSection={<IconWithProgress isLoading={isLoading} />}
          onClick={handleEvaluateRisks}
          disabled={isLoading}
          styles={{
            label: { fontWeight: 600, fontSize: '0.9rem' },
          }}
        >
          Evaluate Risks
        </Button>

        {isLoading && (
          <Box style={{ position: 'relative', display: 'inline-flex', marginRight: 8 }}>
            <Loader size="sm" color="blue" />
            <IconRobot
              size={12}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#3B82F6',
              }}
            />
          </Box>
        )}
      </Box>
    </Box>

    {Object.entries(groupedRisks)
      .filter(([cat]) => cat.toLowerCase() === activeCategory.toLowerCase())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, categoryRisks]) => (
        <Box key={category} style={{ marginBottom: 16 }}>
          {renderRisksByCategory(categoryRisks, category)}
        </Box>
      ))}

    <Modal opened={openDialog} onClose={handleCloseDialog} title="Confirm Discard" centered>
      <Text size="sm">Are you sure you want to discard this security risk?</Text>
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={handleCloseDialog}>Cancel</Button>
        <Button color="red" onClick={handleDiscardRisk}>Discard</Button>
      </Group>
    </Modal>

    <Modal opened={rationaleOpen} onClose={closeRationale} title="Risk Rationale" size="lg" centered>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {rationaleText}
      </Text>
      <Group justify="flex-end" mt="md">
        <Button onClick={closeRationale}>Close</Button>
      </Group>
    </Modal>

    {snackbarOpen && (
      <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>
        <Notification
          withBorder
          withCloseButton
          onClose={handleSnackbarClose}
          color={snackbarSeverity === 'error' ? 'red' : snackbarSeverity === 'success' ? 'green' : 'blue'}
        >
          {snackbarMessage}
        </Notification>
      </div>
    )}
  </div>
);
};

export default SecurityRiskCatalogue;
